import { useKioskNavigation, useOrientation } from '@/hooks';
import { PATHS } from '@/router/paths';
import { TrackingService } from '@/services/analytics/TrackingService';
import { useConfigStore } from '@/stores';
import type { GNBItemType } from '@/types';
import { generateGnbSvgColor } from '@/utils/floor-guide';
import { processSvgData } from '@/utils/svg';
import { clsx } from 'clsx';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './GNBLayout.module.scss';

interface GNBItemProps {
    item: GNBItemType;
    index: number;
    isActive: boolean;
    isFocused: boolean;
}

export const GNBItem: React.FC<GNBItemProps> = ({ item, isActive, isFocused }) => {
    const navigate = useNavigate();
    const { isHorizontal } = useOrientation();
    const { triggerFocusContent } = useKioskNavigation();
    const buttonRef = useRef<HTMLButtonElement>(null);
    const isFAndBPage = item.route === PATHS.FNB.INDEX;
    const contrast = useConfigStore((state) => state.config?.ui?.contrast);
    const screenMode = useConfigStore((state) => state.config?.ui?.screenMode);

    const buttonClassName = clsx('focus-border', styles['gnb-menu__menu-item'], {
        [styles['gnb-menu__menu-item--active']]: isActive,
    });

    const handleClick = useCallback(() => {
        if (item.route) {
            // log menu click (pass target route to resolve menuId)
            if (item.route !== PATHS.HOME.INDEX) {
                TrackingService.trackMenuByRoute(item.route);
            }
            navigate(item.route);

            // Trigger focus to content after navigation (same as Enter key behavior)
            triggerFocusContent();
        }
    }, [item, navigate, triggerFocusContent]);

    const getCssVariableValue = (variableName: string) => {
        const value = window.getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();

        return value;
    };

    const svgUrl = useMemo(() => {
        if (typeof item.icon !== 'string') return undefined;

        if (!isActive && !contrast) {
            return `data:image/svg+xml;base64,${btoa(item.icon)}`;
        }

        const color = getCssVariableValue(generateGnbSvgColor(isActive, contrast)) || '';
        return processSvgData(item.icon, color);
    }, [item.icon, isActive, contrast]);

    // Handle scroll when focused
    useEffect(() => {
        if (isFocused && buttonRef.current) {
            // Force scroll to center the element
            buttonRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'center',
            });
        }
    }, [isFocused]);

    return (
        <button
            ref={buttonRef}
            id={item?.id || ''}
            type="button"
            role="tab"
            className={buttonClassName}
            onClick={handleClick}
            aria-label={item.ariaLabel ?? item.label ?? undefined}
            aria-checked={isActive}
            data-interactive={true}
            data-tts-scope="gnb-menu-item"
            onMouseDown={(e) => e.preventDefault()}
        >
            {item.icon && !(screenMode === 'split' && !isHorizontal) && (
                <span className={styles['gnb-menu__icon']} aria-hidden="true">
                    {typeof item.icon === 'string' ? <img src={svgUrl || undefined} alt="" /> : item.icon}
                </span>
            )}
            <span
                className={clsx(styles['gnb-menu__label'], isFAndBPage ? 'font-number' : '')}
                style={{
                    ...(item?.textColor ? { color: !contrast ? item?.textColor : '#ffffff' } : {}),
                }}
            >
                {item.label}
            </span>
        </button>
    );
};
